import { useCallback, useEffect, useRef, useState } from "react";
import dayjs from "dayjs";
import MainLayout from "../layout/MainLayout";
import FunnelChart from "../components/analytics/FunnelChart";
import AnalyticsTable from "../components/analytics/AnalyticsTable";
import { getActiveCandidates, exportAnalyticsTable } from "../services/analyticsApi";
import { getVacancyOptions } from "../services/vacancyApi";
import { useAlertContext } from "../context/AlertContext";
import { buildCandidateFunnelRows, totalFunnelCount } from "../utils/candidateAnalytics";

export default function AnalyticsPage() {
    const [data, setData] = useState([]);
    const [dateRange, setDateRange] = useState({
        from: dayjs().startOf("month").format("YYYY-MM-DD"),
        to: dayjs().endOf("month").format("YYYY-MM-DD"),
    });
    const { showAlert } = useAlertContext();
    const [loading, setLoading] = useState(true);
    const [vacancies, setVacancies] = useState([]);
    const [selectedVacancyId, setSelectedVacancyId] = useState("");
    const [vacanciesLoading, setVacanciesLoading] = useState(false);
    const [exporting, setExporting] = useState(false);
    const analyticsRequestVersion = useRef(0);

    const fetchAnalytics = useCallback(async () => {
        const requestVersion = ++analyticsRequestVersion.current;
        try {
            setLoading(true);
            const payload = await getActiveCandidates(selectedVacancyId || null);
            if (requestVersion === analyticsRequestVersion.current) {
                setData(buildCandidateFunnelRows(payload?.stats));
            }
        } catch (e) {
            if (requestVersion !== analyticsRequestVersion.current) return;
            console.error("Ошибка загрузки аналитики: ", e);
            setData(buildCandidateFunnelRows({}));
            showAlert(`Ошибка загрузки статистики: ${e.message || e}`, "error");
        } finally {
            if (requestVersion === analyticsRequestVersion.current) {
                setLoading(false);
            }
        }
    }, [selectedVacancyId, showAlert]);

    useEffect(() => {
        fetchAnalytics();
    }, [fetchAnalytics]);

    useEffect(() => () => {
        analyticsRequestVersion.current += 1;
    }, []);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                setVacanciesLoading(true);
                const list = await getVacancyOptions();
                if (!cancelled) {
                    setVacancies(Array.isArray(list) ? list : []);
                }
            } catch (e) {
                if (!cancelled) {
                    setVacancies([]);
                    showAlert(`Ошибка загрузки вакансий: ${e.message || e}`, "error");
                }
            } finally {
                if (!cancelled) setVacanciesLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [showAlert]);

    const handleExport = async () => {
        if (exporting) return;
        if (dateRange.from && dateRange.to && dateRange.from > dateRange.to) {
            showAlert("Дата начала периода не может быть позже даты окончания", "warning");
            return;
        }
        try {
            setExporting(true);
            await exportAnalyticsTable(dateRange.from, dateRange.to, selectedVacancyId || null);
        } catch (e) {
            console.error(e);
            showAlert(String(e.message || e), "error");
        } finally {
            setExporting(false);
        }
    };

    const total = totalFunnelCount(data);
    const activeStatuses = data.filter((row) => Number(row.count) > 0).length;
    const stageGroups = data.reduce((groups, row) => {
        const stage = row.stage || "Другие статусы";
        let group = groups.find((item) => item.name === stage);
        if (!group) {
            group = { name: stage, tone: row.tone, count: 0, rows: [] };
            groups.push(group);
        }
        group.rows.push(row);
        group.count += Number(row.count) || 0;
        return groups;
    }, []);
    const selectedVacancy = vacancies.find((vacancy) => String(vacancy.id) === String(selectedVacancyId));

    return (
        <MainLayout className="bg-slate-50/60 p-4 sm:p-6">
            <div className="mx-auto max-w-[1600px] space-y-6">
                <header className="relative overflow-hidden rounded-3xl bg-slate-900 px-6 py-7 text-white shadow-lg sm:px-8 sm:py-8">
                    <div className="absolute -right-16 -top-20 h-64 w-64 rounded-full bg-amber-400/15 blur-3xl" />
                    <div className="relative flex flex-col gap-6 xl:flex-row xl:items-end xl:justify-between">
                        <div className="max-w-2xl">
                            <p className="text-xs font-bold uppercase tracking-[0.22em] text-amber-300">Аналитика найма</p>
                            <h1 className="mt-2 text-3xl font-bold tracking-tight sm:text-4xl" data-testid="analytics-page-title">
                                Статистика кандидатов
                            </h1>
                            <p className="mt-3 max-w-xl text-sm leading-6 text-slate-300 sm:text-base">
                                Полная картина движения кандидатов по актуальным статусам — без сокращения этапов и изменения правил воронки.
                            </p>
                        </div>
                        <div className="grid w-full grid-cols-1 gap-2 sm:grid-cols-3 sm:gap-3 xl:w-auto">
                            <div className="min-w-0 rounded-2xl border border-white/10 bg-white/10 px-3 py-3 backdrop-blur-sm sm:min-w-[128px] sm:px-4">
                                <span className="block text-[11px] font-semibold uppercase tracking-wide text-slate-300">Кандидаты</span>
                                <strong className="mt-1 block text-2xl font-bold tabular-nums" data-testid="analytics-total">{total}</strong>
                            </div>
                            <div className="min-w-0 rounded-2xl border border-white/10 bg-white/10 px-3 py-3 backdrop-blur-sm sm:min-w-[128px] sm:px-4">
                                <span className="block text-[11px] font-semibold uppercase tracking-wide text-slate-300">Активные статусы</span>
                                <strong className="mt-1 block text-2xl font-bold tabular-nums">{activeStatuses}</strong>
                            </div>
                            <div className="min-w-0 rounded-2xl border border-white/10 bg-white/10 px-3 py-3 backdrop-blur-sm sm:min-w-[128px] sm:px-4">
                                <span className="block text-[11px] font-semibold uppercase tracking-wide text-slate-300">Этапы</span>
                                <strong className="mt-1 block text-2xl font-bold tabular-nums">{stageGroups.length}</strong>
                            </div>
                        </div>
                    </div>
                </header>

                <section className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6" aria-label="Фильтры статистики">
                    <div className="flex flex-col gap-5 xl:flex-row xl:items-end">
                        <div className="grid flex-1 gap-4 sm:grid-cols-2 xl:grid-cols-[160px_160px_minmax(260px,1fr)]">
                            <label className="block text-sm font-semibold text-slate-700">
                                Начало периода
                                <input
                                    type="date"
                                    value={dateRange.from}
                                    onChange={(e) => setDateRange({ ...dateRange, from: e.target.value })}
                                    className="mt-2 min-h-11 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm font-normal text-slate-800 outline-none transition focus:border-amber-400 focus:ring-2 focus:ring-amber-100"
                                    data-testid="analytics-date-from"
                                />
                            </label>
                            <label className="block text-sm font-semibold text-slate-700">
                                Конец периода
                                <input
                                    type="date"
                                    value={dateRange.to}
                                    onChange={(e) => setDateRange({ ...dateRange, to: e.target.value })}
                                    className="mt-2 min-h-11 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm font-normal text-slate-800 outline-none transition focus:border-amber-400 focus:ring-2 focus:ring-amber-100"
                                    data-testid="analytics-date-to"
                                />
                            </label>
                            <label className="block text-sm font-semibold text-slate-700">
                                Вакансия
                                <select
                                    value={selectedVacancyId}
                                    onChange={(e) => setSelectedVacancyId(e.target.value)}
                                    className="mt-2 min-h-11 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm font-normal text-slate-800 outline-none transition focus:border-amber-400 focus:ring-2 focus:ring-amber-100"
                                    data-testid="analytics-vacancy-select"
                                    disabled={vacanciesLoading}
                                >
                                    <option value="">Все вакансии</option>
                                    {vacancies.map((vacancy) => (
                                        <option key={vacancy.id} value={vacancy.id}>
                                            {vacancy.name || `Вакансия #${vacancy.id}`}
                                        </option>
                                    ))}
                                </select>
                            </label>
                        </div>
                        <div className="flex flex-wrap gap-2 xl:justify-end">
                            <button
                                type="button"
                                onClick={fetchAnalytics}
                                disabled={loading}
                                className="inline-flex min-h-11 items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-700 transition hover:border-slate-300 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
                            >
                                <svg aria-hidden="true" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" /></svg>
                                {loading ? "Обновляем…" : "Обновить"}
                            </button>
                            <button
                                type="button"
                                onClick={handleExport}
                                disabled={exporting}
                                className="inline-flex min-h-11 items-center justify-center gap-2 rounded-xl bg-slate-900 px-4 text-sm font-semibold text-white transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-60"
                                data-testid="analytics-export"
                            >
                                <svg aria-hidden="true" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2h-5.5L9 7.5V4H6a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
                                {exporting ? "Готовим файл…" : "Скачать Excel"}
                            </button>
                        </div>
                    </div>
                    <p className="mt-4 text-xs text-slate-500">
                        Воронка: <span className="font-semibold text-slate-700">{selectedVacancy?.name || "все вакансии"}</span>. Период применяется к выгрузке Excel; текущая сводка отражает актуальные статусы.
                    </p>
                </section>

                <section className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
                    <div className="mb-5 flex flex-wrap items-end justify-between gap-3">
                        <div>
                            <p className="text-xs font-bold uppercase tracking-[0.16em] text-slate-400">Структура воронки</p>
                            <h2 className="mt-1 text-xl font-bold text-slate-900">Сводка по этапам и статусам</h2>
                            <p className="mt-1 text-sm text-slate-500">Новые статусы сгруппированы по этапам и больше не уменьшают диаграмму.</p>
                        </div>
                        <span className="rounded-full bg-amber-50 px-3 py-1.5 text-xs font-semibold text-amber-900">
                            Всего кандидатов: {total}
                        </span>
                    </div>

                    {loading ? (
                        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
                            {[0, 1, 2, 3].map((item) => <div key={item} className="h-36 animate-pulse rounded-2xl bg-slate-100" />)}
                        </div>
                    ) : stageGroups.length > 0 ? (
                        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4" data-testid="analytics-status-cards">
                            {stageGroups.map((group) => (
                                <article key={group.name} className="overflow-hidden rounded-2xl border border-slate-200 bg-slate-50/60">
                                    <div className="flex items-center justify-between gap-3 border-b border-slate-200/80 bg-white px-4 py-3">
                                        <div className="flex min-w-0 items-center gap-2.5">
                                            <span className={`h-2.5 w-2.5 shrink-0 rounded-full ${group.tone === "red" ? "bg-rose-400" : group.tone === "amber" ? "bg-amber-500" : group.tone === "green" ? "bg-emerald-500" : "bg-slate-400"}`} />
                                            <h3 className="truncate text-sm font-bold text-slate-800">{group.name}</h3>
                                        </div>
                                        <strong className="rounded-lg bg-slate-100 px-2.5 py-1 text-sm tabular-nums text-slate-900">{group.count}</strong>
                                    </div>
                                    <ul className="divide-y divide-slate-100 px-4 py-1">
                                        {group.rows.map((row) => (
                                            <li key={row.value || row.name} className="flex items-center justify-between gap-3 py-2.5 text-sm">
                                                <span className="min-w-0 text-slate-600" title={row.name}>{row.name}</span>
                                                <span className={`shrink-0 font-bold tabular-nums ${row.count > 0 ? "text-slate-900" : "text-slate-300"}`}>{row.count}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </article>
                            ))}
                        </div>
                    ) : (
                        <p className="rounded-2xl bg-slate-50 p-8 text-center text-sm text-slate-500">Нет данных</p>
                    )}
                </section>

                <FunnelChart data={data} />
                <AnalyticsTable data={data} />
            </div>
        </MainLayout>
    );
}
